import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FiArrowLeft, FiEdit2, FiMail, FiGlobe, FiLinkedin, FiBriefcase, 
  FiCalendar, FiClock, FiX, FiCheckCircle, FiUserPlus, FiMessageSquare,
  FiTrendingUp, FiMapPin, FiDollarSign, FiUsers, FiBox, FiTag, FiUserCheck, FiTarget
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../../finance/auth/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
const LEAD_STAGES = ['LEAD', 'TALKS', 'MEETING', 'NEGOTIATIONS', 'MQL', 'SQL', 'CONVERSION_TO_CLIENT'];

export default function LeadDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
        const res = await axios.get(`${API_BASE_URL}/product/getAllProducts`, { headers });
        setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []));
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    if (user?.token) {
        fetchLead();
        fetchProducts();
    }
  }, [id, user?.token]);

  const fetchLead = async () => {
    if (!user?.token) return;
    try {
      setLoading(true);
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
      const res = await axios.get(`${API_BASE_URL}/sales/leads/${id}`, { headers });
      setLead(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load lead details');
      navigate('/sales/leads');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    setFormData({
      firstName: lead.firstName || '', lastName: lead.lastName || '', jobTitle: lead.jobTitle || '',
      companyName: lead.companyName || '', email: lead.email || '', numberOfEmployees: lead.numberOfEmployees || '',
      industrySegment: lead.industrySegment || '', linkedinUrl: lead.linkedinUrl || '', websiteUrl: lead.websiteUrl || '',
      countryRegion: lead.countryRegion || '', annualRevenue: lead.annualRevenue || '', 
      linkedinConnectionRequestDate: lead.linkedinConnectionRequestDate || '', ownerType: lead.ownerType || '',
      contactOwner: lead.contactOwner || '', companyOwner: lead.companyOwner || '',
      verticalInterest: lead.verticalInterest || '', lifeCycleStage: lead.lifeCycleStage || '',
      linkedinConnectionStatus: lead.linkedinConnectionStatus || '', linkedinDmStatus: lead.linkedinDmStatus || '',
      followUpDate: lead.followUpDate || '', assignedTeamMemberId: lead.assignedTeamMemberId || user?.id || 1,
      stage: lead.stage || 'LEAD'
    });
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!user?.token) return;

    try {
      setSubmitting(true);
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
      const payload = {
        ...formData,
        numberOfEmployees: formData.numberOfEmployees ? parseInt(formData.numberOfEmployees) : null,
        assignedTeamMemberId: formData.assignedTeamMemberId ? parseInt(formData.assignedTeamMemberId) : user?.id || 1
      };

      await axios.put(`${API_BASE_URL}/sales/leads/${id}`, payload, { headers });
      toast.success('Lead updated successfully');
      setIsEditing(false);
      fetchLead(); // reload data
    } catch (err) {
      console.error(err);
      toast.error('Failed to update lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendFollowUpEmail = async () => {
    try {
      setSendingEmail(true);
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
      await axios.post(`${API_BASE_URL}/sales/leads/${id}/followup/send-email`, {}, { headers });
      toast.success('Follow-up email dispatched successfully!');
      fetchLead();
    } catch (err) {
      console.error(err);
      toast.error('Failed to dispatch follow-up email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) return <div className="p-10 flex justify-center text-slate-500 animate-pulse font-medium">Loading lead details...</div>;
  if (!lead) return null;

  const DetailRow = ({ icon: Icon, label, value, isLink }) => (
    <div className="flex flex-col py-3 border-b border-slate-100 last:border-0">
      <div className="text-sm text-slate-500 flex items-center gap-2 mb-1">
        <Icon size={14} /> {label}
      </div>
      {isLink && value ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-base font-medium text-blue-600 hover:underline">
          {value}
        </a>
      ) : (
        <p className="text-base font-medium text-slate-900">{value || '—'}</p>
      )}
    </div>
  );

  return (
    <div className="w-full font-sans text-slate-800 h-full flex flex-col p-6 md:p-8 pb-12">
      
      {/* Top Header / Actions - Clean & Minimal */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pb-6 mb-6 border-b border-slate-200">
        <button onClick={() => navigate('/sales/leads')} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors cursor-pointer">
          <FiArrowLeft size={16} /> Back to Leads
        </button>
        <div className="grid grid-cols-2 sm:flex items-center gap-2">
          {!isEditing && (
            <>
              <button 
                onClick={handleSendFollowUpEmail} 
                disabled={sendingEmail}
                className="flex items-center justify-center gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all shadow-sm disabled:opacity-50 cursor-pointer"
              >
                <FiMail size={16} /> <span className="truncate">{sendingEmail ? 'Sending...' : 'Follow-up Email'}</span>
              </button>
              <button onClick={openEditModal} className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white px-4 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all active:scale-95 shadow-sm cursor-pointer">
                <FiEdit2 size={16} /> Edit Lead
              </button>
            </>
          )}
        </div>
      </div>

      {!isEditing ? (
        <>
          {/* Hero Profile - Borderless and Integrated */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-10">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 text-3xl font-bold">
            {lead.firstName?.charAt(0) || ''}{lead.lastName?.charAt(0) || ''}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{lead.firstName} {lead.lastName}</h1>
            <p className="text-lg text-slate-600 mt-1 font-medium">
              {lead.jobTitle || 'No Title'} <span className="mx-2 text-slate-300">|</span> <span className="text-slate-800">{lead.companyName || 'Unknown Company'}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-between sm:justify-end gap-3 w-full md:w-auto">
          <div className="flex flex-col items-start sm:items-end">
            <span className="text-xs text-slate-500 font-medium mb-1">Current Stage</span>
            <span className="px-3.5 py-1 rounded-full text-xs sm:text-sm font-bold bg-slate-100 text-slate-800">
              {lead.stage === 'CONVERSION_TO_CLIENT' ? 'CONVERSION TO CLIENT' : lead.stage}
            </span>
          </div>
          <div className="w-px h-10 bg-slate-200 mx-2 hidden md:block"></div>
          <div className="flex flex-col items-end">
            <span className="text-xs text-slate-500 font-medium mb-1">Follow-up Date</span>
            <span className="text-xs sm:text-sm font-bold text-slate-800 flex items-center gap-1.5">
              <FiClock className="text-slate-400" /> {lead.followUpDate || 'Unscheduled'}
            </span>
            {lead.followUpEmailSentDate && (
              <span className="text-[10px] sm:text-[11px] text-emerald-600 font-semibold mt-0.5 flex items-center gap-1">
                <FiCheckCircle size={12} /> Email sent {lead.followUpEmailSentDate}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid - 2 Column Layout without heavy cards */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        
        {/* Left Column: Primary Details */}
        <div className="lg:col-span-8 space-y-10">
          
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
              <FiMail className="text-slate-400" /> Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <DetailRow icon={FiMail} label="Email Address" value={lead.email} />
              <DetailRow icon={FiGlobe} label="Website" value={lead.websiteUrl} isLink />
              <DetailRow icon={FiLinkedin} label="LinkedIn Profile" value={lead.linkedinUrl} isLink />
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
              <FiBriefcase className="text-slate-400" /> Company Firmographics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <DetailRow icon={FiBriefcase} label="Company Name" value={lead.companyName} />
              <DetailRow icon={FiBox} label="Industry" value={lead.industrySegment} />
              <DetailRow icon={FiUsers} label="Company Size" value={lead.numberOfEmployees ? `${lead.numberOfEmployees} employees` : null} />
              <DetailRow icon={FiDollarSign} label="Annual Revenue" value={lead.annualRevenue} />
              <DetailRow icon={FiMapPin} label="Region" value={lead.countryRegion} />
            </div>
          </section>
          
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
              <FiMessageSquare className="text-slate-400" /> LinkedIn Outreach
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <DetailRow icon={FiCalendar} label="Request Date" value={lead.linkedinConnectionRequestDate} />
              <DetailRow icon={FiUserPlus} label="Connection Status" value={lead.linkedinConnectionStatus} />
              <DetailRow icon={FiMessageSquare} label="DM Status" value={lead.linkedinDmStatus} />
            </div>
          </section>

        </div>

        {/* Right Column: Pipeline & Ownership */}
        <div className="lg:col-span-4">
          <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
            <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <FiTrendingUp className="text-slate-400" /> Pipeline & Ownership
            </h2>
            
            <div className="space-y-1">
              <DetailRow icon={FiCheckCircle} label="Lead Status" value={lead.leadStatus} />
              <DetailRow icon={FiTarget} label="Vertical Interest" value={lead.verticalInterest} />
              <DetailRow icon={FiTrendingUp} label="Lifecycle Stage" value={lead.lifeCycleStage} />
              <DetailRow icon={FiTag} label="Owner Type" value={lead.ownerType} />
              <DetailRow icon={FiUserCheck} label="Contact Owner" value={lead.contactOwner} />
              <DetailRow icon={FiBriefcase} label="Company Owner" value={lead.companyOwner} />
            </div>
          </div>
        </div>

      </div>
        </>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col mb-10">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <h2 className="text-xl font-bold text-slate-900">Edit Lead Profile</h2>
          </div>
          
          <div className="p-6 overflow-y-visible flex-1">
            <form id="lead-edit-form" onSubmit={handleEditSubmit} className="space-y-8">
                
                <div>
                  <h3 className="text-base font-bold text-slate-900 mb-4 border-b border-slate-100 pb-2">Primary Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">First Name *</label><input required type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Last Name *</label><input required type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Stage</label><select name="stage" value={formData.stage} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors cursor-pointer">{LEAD_STAGES.map(s => <option key={s} value={s}>{s === 'CONVERSION_TO_CLIENT' ? 'CONVERSION TO CLIENT' : s}</option>)}</select></div>
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-bold text-slate-900 mb-4 border-b border-slate-100 pb-2">Company & Firmographics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name</label><input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Job Title</label><input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Employees</label><input type="number" name="numberOfEmployees" value={formData.numberOfEmployees} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Industry Segment</label><input type="text" name="industrySegment" value={formData.industrySegment} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Annual Revenue</label><input type="text" name="annualRevenue" value={formData.annualRevenue} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Region / Country</label><input type="text" name="countryRegion" value={formData.countryRegion} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div className="md:col-span-1"><label className="block text-sm font-medium text-slate-700 mb-1.5">Website URL</label><input type="url" name="websiteUrl" value={formData.websiteUrl} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div className="md:col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1.5">LinkedIn Profile URL</label><input type="url" name="linkedinUrl" value={formData.linkedinUrl} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-bold text-slate-900 mb-4 border-b border-slate-100 pb-2">Sales Tracking & Ownership</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Follow-up Date</label><input type="date" name="followUpDate" value={formData.followUpDate} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Lead Status</label><input type="text" name="leadStatus" value={formData.leadStatus} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Owner Type</label><input type="text" name="ownerType" value={formData.ownerType} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Owner</label><input type="text" name="contactOwner" value={formData.contactOwner} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Company Owner</label><input type="text" name="companyOwner" value={formData.companyOwner} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Lifecycle Stage</label><input type="text" name="lifeCycleStage" value={formData.lifeCycleStage} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Vertical Interest</label>
                        <select name="verticalInterest" value={formData.verticalInterest} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors cursor-pointer">
                            <option value="">Select Product...</option>
                            {products.map(p => (
                                <option key={p.id} value={p.productname}>{p.productname}</option>
                            ))}
                        </select>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-bold text-slate-900 mb-4 border-b border-slate-100 pb-2">LinkedIn Outreach</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Connection Req Date</label><input type="date" name="linkedinConnectionRequestDate" value={formData.linkedinConnectionRequestDate} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors" /></div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Connection Status</label>
                        <select name="linkedinConnectionStatus" value={formData.linkedinConnectionStatus} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors cursor-pointer">
                            <option value="">Select Status...</option>
                            <option value="pending">Pending</option>
                            <option value="accepted">Accepted</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">DM Status</label>
                        <select name="linkedinDmStatus" value={formData.linkedinDmStatus} onChange={handleInputChange} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-slate-400 focus:bg-white transition-colors cursor-pointer">
                            <option value="">Select Status...</option>
                            <option value="not sent">Not sent</option>
                            <option value="sent">Sent</option>
                            <option value="replied">Replied</option>
                        </select>
                    </div>
                  </div>
                </div>

              </form>
          </div>
          
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
            <button type="button" onClick={() => setIsEditing(false)} className="px-5 py-2.5 text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm">Cancel</button>
            <button type="submit" form="lead-edit-form" disabled={submitting} className="px-5 py-2.5 text-sm font-medium text-white bg-slate-900 hover:bg-black rounded-lg disabled:opacity-50 transition-colors shadow-sm">
              {submitting ? 'Saving Changes...' : 'Save Lead'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
